import React, { useEffect, useState } from "react";
import { Offcanvas, Button, Spinner } from "react-bootstrap";
import { getInvoiceOverview } from "../../afinanceapi";
import "./acc-list.css";

/**
 * InvoicePeek — right-side drawer with a near-full invoice overview:
 * invoice + claim status pills, lines led by their SERVICE dates with
 * approved amounts and inline warnings/rejections (the PostedInvoice "i"
 * detail, without the extra click), payment history including reversed
 * receipts, and the outstanding split.
 *
 * Self-contained and id-driven (fetches its own data) so it can be mounted
 * on any screen — AccountStatements now, the single-ERA view later.
 * backdrop={false} + scroll keep the page behind interactive: clicking
 * another invoice row simply swaps the drawer content in place.
 */
const currency = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
});

// Badge colour per status — same mapping as PostedInvoice.jsx so the pills
// match the invoice page.
const getStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case "draft":
      return "warning";
    case "pending":
      return "info";
    case "update posted":
      return "danger";
    case "posted":
      return "success";
    case "claim - processed":
      return "success";
    case "posted - claim in progress":
      return "secondary";
    case "paid":
      return "primary";
    case "claim in progress":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "dark";
  }
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).slice(0, 10) : d.toLocaleDateString("en-GB");
};

// Service date per line — same precedence as CreateInvoice/PostedInvoice:
// custom range > appointment-set range > legacy single date > invoice date.
const serviceDateLabel = (line, invoiceDate) => {
  const start = line.appointmentCustomStart || line.appointmentStartSet || "";
  const end = line.appointmentCustomEnd || line.appointmentEndSet || "";
  if (start) {
    const sd = fmtDate(start);
    const ed = end ? fmtDate(end) : "";
    return ed && ed !== sd ? `${sd} - ${ed}` : sd;
  }
  return fmtDate(
    line.appointmentDateCustom ||
      line.appointmentDateSet ||
      line.appointmentDate ||
      invoiceDate,
  );
};

const paymentTypeLabel = {
  receipt: "Receipt",
  creditNote: "Credit Note",
  refund: "Refund",
  writeOff: "Write-Off",
};

const paymentTypeIcon = {
  receipt: "fa fa-receipt",
  creditNote: "fa fa-undo",
  refund: "fa fa-money-bill-wave",
  writeOff: "fa fa-ban",
};

// Who paid: split a payment across the Patient / Medical Aid columns.
// Write-offs carry an explicit server-side split; everything else goes to
// the column matching its paymentSource.
const paymentSplit = (p) => {
  const amt = Number(p.amount || 0);
  if (p.type === "writeOff") {
    return {
      patient: Number(p.writeOffPatientAmount || 0),
      medAid: Number(p.writeOffMedAidAmount || 0),
    };
  }
  if (["Patient", "patient"].includes(p.paymentSource))
    return { patient: amt, medAid: 0 };
  if (["Medical Aid", "medicalAid"].includes(p.paymentSource))
    return { patient: 0, medAid: amt };
  return { patient: 0, medAid: 0 }; // unknown source — Total column only
};

// Section heading row doubles as the money-column header — the section
// title sits where a separate h6 used to be, with the Patient / Med Aid /
// Total labels in the same row.
const sectionHead = (title) => (
  <thead>
    <tr className="text-primary">
      <th className="fw-bold" style={{ fontSize: "0.9rem" }}>
        {title}
      </th>
      <th className="text-end fw-semibold">Patient</th>
      <th className="text-end fw-semibold">Med Aid</th>
      <th className="text-end fw-semibold">Total</th>
    </tr>
  </thead>
);

export default function InvoicePeek({
  show,
  onHide,
  invoiceId,
  onOpenFullInvoice,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !invoiceId) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const result = await getInvoiceOverview(invoiceId);
        if (!cancelled) setData(result);
      } catch (e) {
        console.error("Failed to load invoice overview:", e);
        if (!cancelled) {
          setData(null);
          setError("Could not load the invoice overview. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [show, invoiceId]);

  const inv = data?.invoice;
  const claim = data?.claim;
  const isPendingInvoice = ["Draft", "Pending"].includes(inv?.status);
  const outstanding = inv?.outstandingBalance;

  const patientName = inv?.patient
    ? `${inv.patient.patientFirstName || ""} ${inv.patient.patientLastName || ""}`.trim()
    : "";

  // Per-line money: the line row always shows the ORIGINAL billed split,
  // straight from the line's stored patientAmount / medAidAmount (!= null
  // checks — 0 is a valid value). The claim's approved amount renders on
  // its own row below, so it must never replace the original here. Legacy
  // lines without a stored split fall back to inferring it from the
  // claim's approved amount.
  const lineRows = (inv?.lines || []).map((line) => {
    const cl = line.claim;
    const charged = Number(line.rate) || 0;
    let patient =
      line.patientAmount != null ? Number(line.patientAmount) || 0 : null;
    let medAid =
      line.medAidAmount != null ? Number(line.medAidAmount) || 0 : null;
    if (medAid == null && patient != null) medAid = charged - patient;
    if (patient == null && medAid != null) patient = charged - medAid;
    if (medAid == null && cl && cl.approvedNettAmount != null) {
      medAid = Number(cl.approvedNettAmount) || 0;
      patient = charged - medAid;
    }
    patient = patient || 0;
    const hasWarnings = (cl?.warnings || []).length > 0;
    const isRejected =
      !!cl && (cl.status === "R" || Number(cl.approvedNettAmount || 0) === 0);

    // ERA remittance verdict for the line (matched ERAs only): paid in
    // full, not paid at all, or partially paid — with the scheme's reasons.
    const era = line.era || null;
    let eraStatus = null;
    if (era) {
      const eraPaid = Number(era.paidAmount || 0);
      const eraClaimed = Number(era.claimedAmount || 0);
      eraStatus =
        eraPaid <= 0
          ? "notpaid"
          : eraPaid + 0.005 < eraClaimed
            ? "partial"
            : "paid";
    }
    // Per-line reconciliation — cumulative paid stamped on the line by
    // allocate-per-line receipts and by ERA auto-allocations. The ERA row
    // already tells the auto-allocated story, so the manually-allocated
    // med-aid portion is whatever was paid per line BEYOND the ERA amount —
    // never the same money twice on one line.
    const paidPatient = Number(line.patientPaid || 0);
    const paidMedAid = Number(line.medAidPaid || 0);
    const hasLinePaid = paidPatient > 0 || paidMedAid > 0;
    const manualMedAid = Math.max(
      0,
      paidMedAid - (era ? Number(era.paidAmount || 0) : 0),
    );
    const paidRowVisible = paidPatient > 0.009 || manualMedAid > 0.009;

    return {
      line,
      cl,
      charged,
      patient,
      medAid,
      hasWarnings,
      isRejected,
      era,
      eraStatus,
      paidPatient,
      paidMedAid,
      hasLinePaid,
      manualMedAid,
      paidRowVisible,
    };
  });

  // Info callout: money was received against this invoice but nothing is
  // recorded per line (older invoices / whole-invoice receipts) — tell the
  // user so they don't hunt for per-line Paid rows that can't exist.
  const noLineAllocation =
    (inv?.payments || []).length > 0 &&
    !lineRows.some((r) => r.era || r.hasLinePaid);

  // Net receipts per column for the Payments footer — refunds subtract from
  // the money received; reversed receipts are excluded (already backed out).
  const paymentTotals = (inv?.payments || []).reduce(
    (acc, p) => {
      const split = paymentSplit(p);
      const sign = p.type === "refund" ? -1 : 1;
      return {
        patient: acc.patient + sign * split.patient,
        medAid: acc.medAid + sign * split.medAid,
        total: acc.total + sign * Number(p.amount || 0),
      };
    },
    { patient: 0, medAid: 0, total: 0 },
  );
  const fmtNet = (v) =>
    v > 0 ? `(${currency.format(v)})` : v < 0 ? currency.format(-v) : "";

  const lineTotals = lineRows.reduce(
    (acc, r) => ({
      patient: acc.patient + (r.patient || 0),
      medAid: acc.medAid + (r.medAid || 0),
      charged: acc.charged + r.charged,
      hasMedAid: acc.hasMedAid || r.medAid != null,
    }),
    { patient: 0, medAid: 0, charged: 0, hasMedAid: false },
  );

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      scroll
      backdrop={false}
      className="invoice-peek acc-list-bare"
    >
      <Offcanvas.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="text-primary"
      >
        <Offcanvas.Title as="h6" className="m-0">
          {patientName ? `${patientName} · ` : ""}Invoice —{" "}
          {inv?.invoiceNumber || "…"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="d-flex flex-column p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading invoice…</p>
          </div>
        ) : error ? (
          <p className="text-danger p-3">{error}</p>
        ) : !inv ? (
          <p className="p-3">No invoice selected.</p>
        ) : (
          <>
            <div className="flex-grow-1 overflow-auto p-3">
              {/* Status pills + invoice/created dates in one row */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge rounded-pill bg-${getStatusVariant(inv.status)}`}
                  style={{ fontSize: "0.7rem" }}
                >
                  Invoice: {inv.status || "-"}
                </span>
                {claim?.status ? (
                  <span
                    className={`badge rounded-pill text-wrap bg-${getStatusVariant(claim.status)}`}
                    style={{ fontSize: "0.7rem" }}
                  >
                    Claim: {claim.status}
                  </span>
                ) : (
                  !isPendingInvoice && (
                    <span
                      className="badge rounded-pill bg-light text-dark border"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Claim: Pending Submission
                    </span>
                  )
                )}
                <span className="small ms-1">
                  <span className="fw-semibold">Invoice Date:</span>{" "}
                  {fmtDate(inv.invoiceDate)}
                  {inv.createdAt ? (
                    <>
                      {" · "}
                      <span className="fw-semibold">Created Date:</span>{" "}
                      {fmtDate(inv.createdAt)}
                    </>
                  ) : (
                    ""
                  )}
                </span>
              </div>

              {/* Lines — claim result row sits above each line, exactly like
                  the invoice page: reasons (code — description) on the left
                  with the approved price beside them in the Med Aid column.
                  No status labels — the text colour carries warnings (teal)
                  vs rejections (red); a clean processed line shows only its
                  approved price. */}
              {lineRows.length === 0 ? (
                <p className="small">No line items.</p>
              ) : (
                <table className="w-100 small invoice-peek-cols mb-0">
                  {sectionHead("Lines")}
                  <tbody>
                    {lineRows.map((r, li) => {
                      const { line, cl, era, eraStatus } = r;
                      const eraColor =
                        eraStatus === "paid"
                          ? "text-primary"
                          : eraStatus === "notpaid"
                            ? "text-danger"
                            : "text-secondary";
                      const eraReason = (era?.reasonCodes || [])
                        .map((rc) =>
                          [rc.code, rc.description].filter(Boolean).join(" — "),
                        )
                        .filter(Boolean)
                        .join("; ");
                      return (
                        <React.Fragment key={`line-${li}`}>
                          <tr
                            className={`invoice-peek-line-row${
                              cl || era || r.paidRowVisible ? " no-sep" : ""
                            }`}
                          >
                            <td>
                              <div className="fw-semibold text-primary">
                                {serviceDateLabel(line, inv.invoiceDate)}
                              </div>
                              {/* Everything under the service date is
                                  indented so the date reads as the group
                                  header for line + claim + ERA + paid rows. */}
                              <div className="ms-2">
                                <span className="fw-semibold">
                                  {line.tariffCode || line.nappiCode || "-"}
                                </span>{" "}
                                {line.description || ""}
                                {Number(line.quantity ?? 1) !== 1
                                  ? ` × ${line.quantity}`
                                  : ""}
                              </div>
                              {cl && (
                                <>
                                  {/* Non-zero approved components (the "i" detail) */}
                                  <div className="text-muted ms-2">
                                    {Number(cl.approvedDiscount || 0) !== 0 && (
                                      <span className="me-3">
                                        Discount{" "}
                                        {currency.format(
                                          Number(cl.approvedDiscount),
                                        )}
                                      </span>
                                    )}
                                    {Number(cl.approvedCopay || 0) !== 0 && (
                                      <span className="me-3">
                                        Copay{" "}
                                        {currency.format(
                                          Number(cl.approvedCopay),
                                        )}
                                      </span>
                                    )}
                                    {Number(cl.approvedLevy || 0) !== 0 && (
                                      <span className="me-3">
                                        Levy{" "}
                                        {currency.format(
                                          Number(cl.approvedLevy),
                                        )}
                                      </span>
                                    )}
                                    {Number(cl.overCharged || 0) !== 0 && (
                                      <span className="me-3">
                                        Over-charged{" "}
                                        {currency.format(
                                          Number(cl.overCharged),
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  {(cl.modifiers || []).map((mod, mi) => (
                                    <div key={`m-${li}-${mi}`} className="ms-2">
                                      <span className="fw-semibold">
                                        Modifier{" "}
                                        {mod.modifierCode || mod.modifier || ""}
                                      </span>
                                      {(mod.warnings || []).map((w, wi) => (
                                        <div
                                          key={`mw-${mi}-${wi}`}
                                          className="text-secondary ms-2"
                                        >
                                          {w.code} — {w.description}
                                        </div>
                                      ))}
                                      {(mod.rejections || []).map((rj, ri) => (
                                        <div
                                          key={`mr-${mi}-${ri}`}
                                          className="text-danger ms-2"
                                        >
                                          {rj.code} — {rj.description}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </>
                              )}
                            </td>
                            <td className="acc-list-num">
                              {r.patient > 0
                                ? currency.format(r.patient)
                                : ""}
                            </td>
                            <td className="acc-list-num">
                              {r.medAid != null && r.medAid > 0
                                ? currency.format(r.medAid)
                                : ""}
                            </td>
                            <td className="acc-list-num fw-semibold">
                              {currency.format(r.charged)}
                            </td>
                          </tr>
                          {cl && (
                            <tr
                              className={`invoice-peek-claim-row${
                                era || r.paidRowVisible ? " no-sep" : ""
                              }`}
                            >
                              {/* The approved amount is the scheme's
                                  "promise", not money received — it lives in
                                  the description text, directly after the
                                  reason; the 3 amount columns stay reserved
                                  for actual invoice/paid figures. */}
                              <td colSpan={4} className="ps-2">
                                {(() => {
                                  const reasons = [
                                    ...(cl.warnings || []).map((w) => ({
                                      ...w,
                                      tone: "text-secondary",
                                    })),
                                    ...(cl.rejections || []).map((rj) => ({
                                      ...rj,
                                      tone: "text-danger",
                                    })),
                                  ];
                                  const approvedText = `Approved ${currency.format(
                                    Number(cl.approvedNettAmount || 0),
                                  )}`;
                                  if (reasons.length === 0) {
                                    // Same rule as PostedInvoice's
                                    // getStatusVariantLines: rejected line or
                                    // zero approved → red, else primary.
                                    return (
                                      <div
                                        className={
                                          r.isRejected
                                            ? "text-danger"
                                            : "text-primary"
                                        }
                                      >
                                        <span className="fw-semibold">
                                          Claim:
                                        </span>{" "}
                                        Processed — {approvedText}
                                      </div>
                                    );
                                  }
                                  return reasons.map((rc, ri) => (
                                    <div
                                      key={`rc-${li}-${ri}`}
                                      className={rc.tone}
                                    >
                                      <span className="fw-semibold">
                                        Claim:
                                      </span>{" "}
                                      {rc.code} — {rc.description}
                                      {ri === reasons.length - 1
                                        ? ` — ${approvedText}`
                                        : ""}
                                    </div>
                                  ));
                                })()}
                              </td>
                            </tr>
                          )}
                          {era && (
                            <tr
                              className={`invoice-peek-era-row${
                                r.paidRowVisible ? " no-sep" : ""
                              } ${eraColor}`}
                            >
                              <td colSpan={2} className="ps-2">
                                <span className="fw-semibold">ERA:</span>{" "}
                                {eraStatus === "paid"
                                  ? "Paid — Auto Allocated"
                                  : eraStatus === "notpaid"
                                    ? `Not Paid${eraReason ? ` — ${eraReason}` : ""}`
                                    : `Partially Paid — Auto Allocated${eraReason ? ` — ${eraReason}` : ""}`}
                              </td>
                              <td colSpan={2} className="acc-list-num fw-semibold">
                                {eraStatus === "notpaid"
                                  ? ""
                                  : currency.format(Number(era.paidAmount || 0))}
                              </td>
                            </tr>
                          )}
                          {r.manualMedAid > 0.009 && (
                            <tr
                              className={`invoice-peek-paid-row text-primary${
                                r.paidPatient > 0.009 ? " no-sep" : ""
                              }`}
                            >
                              <td className="ps-2">
                                <span className="fw-semibold">Paid:</span> By
                                Medical Aid — Manually Allocated
                              </td>
                              <td></td>
                              <td className="acc-list-num">
                                ({currency.format(r.manualMedAid)})
                              </td>
                              <td className="acc-list-num">
                                ({currency.format(r.manualMedAid)})
                              </td>
                            </tr>
                          )}
                          {r.paidPatient > 0.009 && (
                            <tr className="invoice-peek-paid-row text-primary">
                              <td className="ps-2">
                                <span className="fw-semibold">Paid:</span> By
                                Patient — Manually Allocated
                              </td>
                              <td className="acc-list-num">
                                ({currency.format(r.paidPatient)})
                              </td>
                              <td></td>
                              <td className="acc-list-num">
                                ({currency.format(r.paidPatient)})
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold invoice-peek-total-row">
                      <td>Invoice Total</td>
                      <td className="acc-list-num">
                        {lineTotals.patient
                          ? currency.format(lineTotals.patient)
                          : ""}
                      </td>
                      <td className="acc-list-num">
                        {lineTotals.hasMedAid
                          ? currency.format(lineTotals.medAid)
                          : ""}
                      </td>
                      <td className="acc-list-num">
                        {currency.format(lineTotals.charged)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}

              {/* Payments — Patient / Med Aid / Total columns so it's clear
                  who paid what. Receipts/credit notes/write-offs render in
                  brackets (money off the invoice); refunds render plain. */}
              {((inv.payments || []).length > 0 ||
                (inv.reversedReceipts || []).length > 0) && (
                <>
                  <table className="w-100 small invoice-peek-cols mb-0 mt-3">
                    {sectionHead("Payments")}
                    <tbody>
                      {(inv.payments || []).map((p, pi) => {
                        const split = paymentSplit(p);
                        const isRefund = p.type === "refund";
                        const fmtCell = (v) =>
                          v
                            ? isRefund
                              ? currency.format(v)
                              : `(${currency.format(v)})`
                            : "";
                        return (
                          <tr key={`pay-${pi}`}>
                            <td>
                              <i
                                className={`${paymentTypeIcon[p.type] || "fa fa-receipt"} text-primary me-2`}
                              />
                              {paymentTypeLabel[p.type] || "Receipt"} {p.number}
                              {p.date ? ` · ${fmtDate(p.date)}` : ""}
                            </td>
                            <td className="acc-list-num text-primary">
                              {fmtCell(split.patient)}
                            </td>
                            <td className="acc-list-num text-primary">
                              {fmtCell(split.medAid)}
                            </td>
                            <td className="acc-list-num text-primary">
                              {isRefund
                                ? currency.format(Number(p.amount || 0))
                                : `(${currency.format(Number(p.amount || 0))})`}
                            </td>
                          </tr>
                        );
                      })}
                      {(inv.reversedReceipts || []).map((p, pi) => (
                        <tr key={`rev-${pi}`} className="text-danger">
                          <td>
                            <i className="fa fa-rotate-left me-2" />
                            Receipt {p.number} reversed
                            {p.reversedAt ? ` · ${fmtDate(p.reversedAt)}` : ""}
                          </td>
                          <td className="acc-list-num">
                            {p.patientAmount
                              ? currency.format(Number(p.patientAmount))
                              : ""}
                          </td>
                          <td className="acc-list-num">
                            {p.medAidAmount
                              ? currency.format(Number(p.medAidAmount))
                              : ""}
                          </td>
                          <td className="acc-list-num">
                            {currency.format(Number(p.amount || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-semibold invoice-peek-total-row">
                        <td>Total Receipts</td>
                        <td className="acc-list-num">
                          {fmtNet(paymentTotals.patient)}
                        </td>
                        <td className="acc-list-num">
                          {fmtNet(paymentTotals.medAid)}
                        </td>
                        <td className="acc-list-num">
                          {fmtNet(paymentTotals.total) ||
                            currency.format(0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}

              {/* Outstanding — same Patient / Med Aid / Total columns */}
              <table className="w-100 small invoice-peek-cols mb-0 mt-3">
                {sectionHead("Outstanding")}
                <tbody>
                  <tr className="fw-bold">
                    <td>Total Outstanding</td>
                    <td className="acc-list-num">
                      {currency.format(
                        Number(outstanding?.patientOutstanding || 0),
                      )}
                    </td>
                    <td className="acc-list-num">
                      {currency.format(
                        Number(outstanding?.medAidOutstanding || 0),
                      )}
                    </td>
                    <td className="acc-list-num">
                      {currency.format(
                        Number(outstanding?.totalOutstanding || 0),
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              {noLineAllocation && (
                <div className="posted-invoice-info mt-3 small">
                  <i className="fa-solid fa-circle-info me-2" />
                  Payments on this invoice were allocated at invoice level —
                  no line-by-line allocation was recorded (older invoices and
                  whole-invoice receipts don't carry per-line detail), so no
                  Paid rows appear under the lines above. The Payments
                  section shows the full amounts received.
                </div>
              )}
            </div>

            {/* Sticky footer */}
            <div className="border-top p-3 d-flex gap-2 bg-white">
              <Button
                variant="primary"
                onClick={() => onOpenFullInvoice?.(inv)}
                style={{ minWidth: 160 }}
              >
                <i className="fa-solid fa-file-invoice me-2" />
                Open full invoice
              </Button>
              <Button
                variant="outline-secondary"
                onClick={onHide}
                style={{ minWidth: 100 }}
              >
                Close
              </Button>
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
